/* global axios */
import ApiClient from './ApiClient';

// Motivo de pérdida (P10): solo se manda si se eligió algo en el modal. Sin
// `lostReasonId` el CRM lo guarda como "sin motivo" (si la etapa es perdida).
const lostParams = ({ lostReasonId, lostNote } = {}) => ({
  ...(lostReasonId ? { lost_reason_id: lostReasonId } : {}),
  ...(lostNote ? { lost_note: lostNote } : {}),
});

class PipelinesAPI extends ApiClient {
  constructor() {
    super('pipelines', { accountScoped: true });
  }

  // Lista de pipelines. Por defecto el CRM devuelve solo los activos (tablero,
  // conversación, Informes, Tareas); Ajustes → Pipelines pide también los
  // inactivos para poder reactivarlos (solo vale para administradores).
  get({ includeInactive = false } = {}) {
    return axios.get(this.url, {
      params: includeInactive ? { include_inactive: true } : undefined,
    });
  }

  // Get all stages for a pipeline
  getStages(pipelineId) {
    return axios.get(`${this.url}/${pipelineId}/stages`);
  }
  
  // Get all stages with contacts for a pipeline (paginated).
  // search, enteredAfter and enteredBefore are all server-side filters on this same
  // endpoint and combine freely. enteredAfter/enteredBefore are UNIX seconds; dateBasis
  // ('pipeline' default, or 'stage') picks whether they bound the contact's first entry
  // into the pipeline or its entry into the stage being listed.
  getStagesWithContacts(
    pipelineId,
    { perPage = 50, search, enteredAfter, enteredBefore, dateBasis } = {}
  ) {
    return axios.get(`${this.url}/${pipelineId}/stages`, {
      params: {
        include_contacts: true,
        per_page: perPage,
        search: search || undefined,
        entered_after: enteredAfter ?? undefined,
        entered_before: enteredBefore ?? undefined,
        date_basis: dateBasis || undefined,
      },
    });
  }

  // Load more contacts for a specific stage
  getStageContacts(
    pipelineId,
    stageId,
    { page = 1, perPage = 50, enteredAfter, enteredBefore, dateBasis } = {}
  ) {
    return axios.get(`${this.url}/${pipelineId}/stages/${stageId}/contacts`, {
      params: {
        page,
        per_page: perPage,
        entered_after: enteredAfter ?? undefined,
        entered_before: enteredBefore ?? undefined,
        date_basis: dateBasis || undefined,
      },
    });
  }

  // Create a new stage in a pipeline
  createStage(pipelineId, stageData) {
    return axios.post(`${this.url}/${pipelineId}/stages`, stageData);
  }

  // Update a stage
  updateStage(pipelineId, stageId, stageData) {
    return axios.patch(`${this.url}/${pipelineId}/stages/${stageId}`, stageData);
  }

  // Delete a stage
  deleteStage(pipelineId, stageId) {
    return axios.delete(`${this.url}/${pipelineId}/stages/${stageId}`);
  }

  // Reorder stages
  reorderStages(pipelineId, stageIds) {
    return axios.post(`${this.url}/${pipelineId}/reorder_stages`, {
      stage_ids: stageIds,
    });
  }

  // Duplicate a pipeline
  duplicate(pipelineId) {
    return axios.post(`${this.url}/${pipelineId}/duplicate`);
  }

  // Move contact to stage
  moveContactToStage(pipelineId, stageId, contactId, notes = '', lost = {}) {
    return axios.post(
      `${this.url}/${pipelineId}/stages/${stageId}/move_contact`,
      {
        contact_id: contactId,
        notes,
        ...lostParams(lost),
      }
    );
  }

  // Bulk move contacts to stage
  bulkMoveContactsToStage(
    pipelineId,
    stageId,
    contactIds,
    notes = '',
    lost = {}
  ) {
    return axios.post(
      `${this.url}/${pipelineId}/stages/${stageId}/bulk_move_contacts`,
      {
        contact_ids: contactIds,
        notes,
        ...lostParams(lost),
      }
    );
  }

  // Import all CRM contacts to stage
  importAllContactsToStage(pipelineId, stageId) {
    return axios.post(`${this.url}/${pipelineId}/stages/${stageId}/import_all_contacts`);
  }

  // Contacts the current user may add to a pipeline (only their assigned ones
  // when the account restricts agents with pipeline_assigned_only_for_agent)
  getAssignableContacts(query = '') {
    return axios.get(`${this.url}/assignable_contacts`, {
      params: { q: query || undefined },
    });
  }

  // Remove contact from stage
  removeContactFromStage(pipelineId, stageId, contactId) {
    return axios.delete(`${this.url}/${pipelineId}/stages/${stageId}/remove_contact`, {
      data: { contact_id: contactId }
    });
  }
}

export default new PipelinesAPI();